<script setup lang="ts">
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { usePurchaseStore } from '@/dashboard/store/purchaseStore';
import Field from '@/dashboard/vue/components/common/Field.vue';
import Button from '@/dashboard/vue/components/common/Button.vue';
import LoaderIcon from '@/dashboard/vue/components/icons/LoaderIcon.vue';
import { getCountries } from '@/dashboard/utils/getCountries';

const purchaseStore = usePurchaseStore();

const { name, countryCode, zipCode, loading } = storeToRefs(purchaseStore);
const countries = ref(getCountries());
const isValid = ref(true);

async function next() {
  if (zipCode.value.length <= 0) {
    isValid.value = false;
    return;
  }

  if (countryCode.value === 'US' && !/(^\d{5}$)|(^\d{5}-\d{4}$)/.test(zipCode.value)) {
    isValid.value = false;
    return;
  }

  isValid.value = true;

  const address = await purchaseStore.createAddress();
  if (!address) {
    return;
  }

  const result = await purchaseStore.getTransactionPreview();

  if (result) {
    purchaseStore.goNext();
  }
}
</script>

<template>
  <div class="flex h-[736px] w-[1080px] justify-center">
    <div class="flex max-w-[480px] flex-1 flex-col pt-[88px]">
      <span class="text-content-xl mb-[83px] text-black dark:text-gray-500">Billing address</span>

      <div class="mb-[83px] flex flex-col gap-[24px]">
        <Field v-model="name" label="Your name" class="text-left" placeholder="Name" :readonly="true" />

        <div class="flex flex-col gap-xs text-left">
          <span class="text-caption-s text-gray-700 dark:text-gray-500">Country</span>
          <select
            v-model="countryCode"
            class="select select-large-icon flex h-[56px] items-center justify-between rounded-8 border-[1px] border-solid border-gray-400 bg-transparent pl-[20px] pr-s text-black outline-none dark:border-gray-700 dark:text-white"
          >
            <option v-for="item in countries" :key="item.country.name" :value="item.code">{{ item.country.name }}</option>
          </select>
        </div>

        <Field v-model="zipCode" label="Postal/ZIP code" class="text-left" placeholder="Write here" :error="!isValid" />
      </div>

      <div class="mb-[65px] flex justify-center">
        <div class="flex w-[240px]">
          <Button :on-click="() => next()" class="button-primary dark:button-colored h-[48px]" :disabled="loading">
            <span v-if="!loading" class="button__text">Next</span>
            <LoaderIcon v-else class="animate-spin stroke-white" />
          </Button>
        </div>
      </div>

      <div class="mb-m flex justify-center">
        <button class="text-caption-s text-gray-500 dark:text-gray-650" @click="purchaseStore.goPrev()">
          <span>← </span>
          <span class="underline underline-offset-2">Back</span>
        </button>
      </div>
    </div>
  </div>
</template>
